import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";
import { DocsSidebarEntry } from "../../types/analytics";
import { dedent } from "../../utils/analytics";
import { MarkdownContent } from "../SharedStyled";

const SidebarContainer = styled.div<{ isOpen: boolean }>`
    width: ${props => props.isOpen ? "320px" : "0"};
    min-width: ${props => props.isOpen ? "320px" : "0"};
    transition: width 0.2s ease, min-width 0.2s ease;
    overflow-y: auto;
    overflow-x: hidden;
    border-left: ${props => props.isOpen ? "1px solid #444" : "none"};
    padding: ${props => props.isOpen ? "16px 12px" : "0"};
    flex-shrink: 0;
    align-self: flex-start;
    position: sticky;
    top: 56px;
    max-height: calc(100vh - 56px);
    font-size: 0.875rem;
`;

const EntryDetails = styled.details`
    margin-bottom: 16px;

    summary {
        font-weight: bold;
        cursor: pointer;
        padding: 4px 0;
        user-select: none;

        &:hover {
            opacity: 0.8;
        }
    }
`;

const SchemaFieldName = styled.h6`
    margin-top: 12px;
    margin-bottom: 2px;
    font-size: 0.8125rem;
    font-family: monospace;
    color: inherit;
`;

const SchemaFieldDesc = styled.p`
    font-size: 0.8125rem;
    color: #adb5bd;
    margin-bottom: 4px;
    
`;

const SchemaSectionTitle = styled.h5`
    margin-top: 14px;
    margin-bottom: 6px;
    font-size: 0.8125rem;
    font-weight: 700;
    color: inherit;
`;

type DocsSidebarProps = {
    isOpen: boolean;
    entries: DocsSidebarEntry[];
};


const DocsSidebar: React.FC<DocsSidebarProps> = ({ isOpen, entries }) => {
    return (
        <SidebarContainer isOpen={isOpen} data-export-ignore>
            {isOpen && entries.map(entry => (
                <EntryDetails key={entry.widgetKey}>
                    <summary>{entry.displayName}</summary>
                    <MarkdownContent>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{dedent(entry.description)}</ReactMarkdown>
                    </MarkdownContent>
                    {entry.schema && (() => {
                        const schemaEntries = Object.entries(entry.schema);
                        const regularEntries = schemaEntries.filter(([field]) => !field.startsWith("metric_definitions."));
                        const metricDefinitionEntries = schemaEntries.filter(([field]) => field.startsWith("metric_definitions."));

                        return (
                            <>
                                {regularEntries.map(([field, meta]) => (
                                    <React.Fragment key={field}>
                                        <SchemaFieldName>{field}</SchemaFieldName>
                                        <SchemaFieldDesc>{meta.description}</SchemaFieldDesc>
                                    </React.Fragment>
                                ))}

                                {metricDefinitionEntries.length > 0 && (
                                    <SchemaSectionTitle>Metric Definitions</SchemaSectionTitle>
                                )}

                                {metricDefinitionEntries.map(([field, meta]) => {
                                    const metricKey = field.replace("metric_definitions.", "");
                                    return (
                                        <React.Fragment key={field}>
                                            <SchemaFieldName>{metricKey}</SchemaFieldName>
                                            <SchemaFieldDesc>{meta.description}</SchemaFieldDesc>
                                        </React.Fragment>
                                    );
                                })}
                            </>
                        );
                    })()}
                </EntryDetails>
            ))}
        </SidebarContainer>
    );
};

export default DocsSidebar;
